import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaBell } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import WebService from "../Services/WebService";
import navStyles from "./navbar.module.css";
import styles from "./NotificationBell.module.css";

interface AppNotification {
  id: number;
  title: string;
  message?: string;
  type?: string;
  reference_type?: string;
  reference_id?: number;
  is_read: boolean;
  created_at?: string;
}

const POLL_MS = 60_000;

function timeAgo(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return d.toLocaleDateString();
}

/** In-app notifications (e.g. "New task assigned"). Polls the unread count every minute. */
const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const fetchUnread = useCallback(async () => {
    if (!localStorage.getItem("token")) return;
    try {
      const res: any = await WebService.getAPI({
        action: `/v1/door-management/notifications/unread-count`,
        isShowError: false,
      });
      setUnread(Number(res?.data?.unread_count) || 0);
    } catch {
      // keep last known count
    }
  }, []);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res: any = await WebService.getAPI({
        action: `/v1/door-management/notifications?page_number=1&limit=20`,
        isShowError: false,
      });
      setItems(Array.isArray(res?.data?.notifications) ? res.data.notifications : []);
      setUnread(Number(res?.data?.unread_count) || 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, POLL_MS);
    return () => clearInterval(id);
  }, [fetchUnread]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList();
  };

  const handleClick = async (n: AppNotification) => {
    if (!n.is_read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnread((c) => Math.max(0, c - 1));
      WebService.patchAPI({
        action: `/v1/door-management/notifications/${n.id}/read`,
        isShowError: false,
      }).catch(() => {});
    }
    if (n.reference_type === "TASK") {
      setOpen(false);
      navigate(n.reference_id ? `/task-management?task=${n.reference_id}` : "/task-management");
    }
  };

  const markAllRead = async () => {
    try {
      await WebService.patchAPI({ action: `/v1/door-management/notifications/read-all` });
      setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
      setUnread(0);
    } catch {
      // WebService shows the error toast
    }
  };

  return (
    <div className={styles.bellWrapper} ref={wrapperRef} title="Notifications">
      <FaBell size={19} onClick={toggle} />
      {unread > 0 && (
        <span className={navStyles.cartBadge} onClick={toggle}>
          {unread > 99 ? "99+" : unread}
        </span>
      )}

      {open && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span>Notifications</span>
            <button className={styles.markAll} onClick={markAllRead} disabled={unread === 0}>
              Mark all read
            </button>
          </div>
          <div className={styles.list}>
            {loading && items.length === 0 ? (
              <div className={styles.empty}>Loading...</div>
            ) : items.length === 0 ? (
              <div className={styles.empty}>No notifications yet</div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`${styles.item} ${n.is_read ? "" : styles.unread}`}
                  onClick={() => handleClick(n)}
                >
                  <div className={styles.itemTitle}>{n.title}</div>
                  {n.message && <div className={styles.itemMessage}>{n.message}</div>}
                  <div className={styles.itemTime}>{timeAgo(n.created_at)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
